// Utility functions for venue opening hours

// Helper function to format time strings by removing :00 when minutes are zero
export const formatTimeString = (timeStr: string): string => {
  return timeStr.replace(/:00(?=\s*[AP]M)/gi, "");
};

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

// Day and minute of the day on the venue's own clock. Hours are local to the
// venue, so a Tokyo cafe is read on Tokyo time. Without a recorded zone (or
// with one Intl doesn't know) the device clock is used, as before.
const venueClock = (timezone?: string): { day: string; minutes: number } => {
  const now = new Date();
  if (timezone) {
    try {
      const parts = Object.fromEntries(
        new Intl.DateTimeFormat("en-US", {
          timeZone: timezone,
          weekday: "long",
          hour: "2-digit",
          minute: "2-digit",
          hourCycle: "h23",
        })
          .formatToParts(now)
          .map(part => [part.type, part.value]),
      );
      return { day: parts.weekday, minutes: Number(parts.hour) * 60 + Number(parts.minute) };
    } catch {
      // Unknown zone: fall through to the device clock.
    }
  }
  return { day: DAY_NAMES[now.getDay()], minutes: now.getHours() * 60 + now.getMinutes() };
};

// Helper function to get current day of the week (at the venue when a zone is given)
export const getCurrentDay = (timezone?: string): string => venueClock(timezone).day;

// Helper function to get abbreviated day name
export const getAbbreviatedDay = (fullDay: string): string => {
  const dayAbbreviations: Record<string, string> = {
    Sunday: "Sun",
    Monday: "Mon",
    Tuesday: "Tue",
    Wednesday: "Wed",
    Thursday: "Thu",
    Friday: "Fri",
    Saturday: "Sat",
  };
  return dayAbbreviations[fullDay] || fullDay;
};

// Helper function to parse time string to minutes since midnight
export const parseTimeToMinutes = (timeStr: string): number | null => {
  const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (!match) return null;

  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const period = match[3].toUpperCase();

  if (period === "PM" && hours !== 12) {
    hours += 12;
  } else if (period === "AM" && hours === 12) {
    hours = 0;
  }

  return hours * 60 + minutes;
};

export interface OpenStatus {
  isOpen: boolean;
  nextChangeTime: string | null;
  nextChangeDay: string | null;
  nextChangeIsOpening: boolean;
}

// Helper function to check if currently open and get next status change
export const getOpenStatus = (lines: string[], timezone?: string): OpenStatus => {
  const { day: currentDay, minutes: currentMinutes } = venueClock(timezone);
  const dayOrder = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const currentDayIndex = dayOrder.indexOf(currentDay);

  // Parse all hours - expand day ranges into individual days
  const schedule: Array<{
    day: string;
    dayIndex: number;
    hours: string;
    openTime: number | null;
    closeTime: number | null;
  }> = [];

  for (const line of lines) {
    const match = line.match(/^(.+?):\s*(.+)$/);
    if (match) {
      const dayPart = match[1].trim();
      const hours = match[2].trim();

      // Check if this is a day range (e.g., "Monday – Wednesday")
      const rangeMatch = dayPart.match(/^(.+?)\s*[-–]\s*(.+)$/);
      const daysToProcess: string[] = [];

      if (rangeMatch) {
        // It's a range - expand it
        const startDay = rangeMatch[1].trim();
        const endDay = rangeMatch[2].trim();
        const startIndex = dayOrder.indexOf(startDay);
        const endIndex = dayOrder.indexOf(endDay);

        if (startIndex !== -1 && endIndex !== -1) {
          // Add all days in the range
          let currentIndex = startIndex;
          while (true) {
            daysToProcess.push(dayOrder[currentIndex]);
            if (currentIndex === endIndex) break;
            currentIndex = (currentIndex + 1) % 7;
          }
        }
      } else {
        // Single day
        daysToProcess.push(dayPart);
      }

      // Process each day
      for (const day of daysToProcess) {
        const dayIndex = dayOrder.indexOf(day);
        if (dayIndex === -1) continue;

        if (hours.toLowerCase().includes("closed")) {
          schedule.push({ day, dayIndex, hours, openTime: null, closeTime: null });
        } else if (hours.toLowerCase().includes("open 24 hours") || hours.toLowerCase().includes("24 hours")) {
          // Handle "Open 24 hours" - store as midnight to midnight
          schedule.push({ day, dayIndex, hours: "Open 24 hours", openTime: 0, closeTime: 1440 });
        } else {
          // Match times with optional AM/PM on the first time
          const timeMatch = hours.match(/(\d{1,2}:\d{2}(?:\s*[AP]M)?)\s*[-–]\s*(\d{1,2}:\d{2}\s*[AP]M)/i);
          if (timeMatch) {
            let openTimeStr = timeMatch[1];
            const closeTimeStr = timeMatch[2];

            // If the opening time doesn't have AM/PM, infer it from context
            if (!/[AP]M/i.test(openTimeStr)) {
              // Extract the period from closing time
              const closePeriod = closeTimeStr.match(/([AP]M)/i)?.[1];
              if (closePeriod) {
                openTimeStr = `${openTimeStr} ${closePeriod}`;
              }
            }

            const openTime = parseTimeToMinutes(openTimeStr);
            let closeTime = parseTimeToMinutes(closeTimeStr);

            // Check for 24-hour operation (12:00 AM – 12:00 AM)
            let normalizedHours: string;
            if (openTime === 0 && closeTime === 0) {
              closeTime = 1440; // 24 hours
              normalizedHours = "Open 24 hours";
            }
            // Handle times that go past midnight
            else if (closeTime !== null && openTime !== null && closeTime < openTime) {
              closeTime += 24 * 60;
              normalizedHours = `${openTimeStr} – ${closeTimeStr}`;
            } else {
              normalizedHours = `${openTimeStr} – ${closeTimeStr}`;
            }

            schedule.push({ day, dayIndex, hours: normalizedHours, openTime, closeTime });
          }
        }
      }
    }
  }

  // Find today's hours
  const todaySchedule = schedule.find(s => s.dayIndex === currentDayIndex);

  // Check for 24-hour operation (midnight to midnight)
  const is24Hours = todaySchedule?.openTime === 0 && todaySchedule?.closeTime === 1440; // 1440 = 24 * 60

  if (is24Hours) {
    return {
      isOpen: true,
      nextChangeTime: null,
      nextChangeDay: null,
      nextChangeIsOpening: false,
    };
  }

  if (!todaySchedule || todaySchedule.openTime === null || todaySchedule.closeTime === null) {
    // Closed today - find next opening
    for (let i = 1; i <= 7; i++) {
      const nextDayIndex = (currentDayIndex + i) % 7;
      const nextDay = schedule.find(s => s.dayIndex === nextDayIndex);
      if (nextDay && nextDay.openTime !== null) {
        const openTimeStr = nextDay.hours.match(/(\d{1,2}:\d{2}\s*[AP]M)/i)?.[1];
        return {
          isOpen: false,
          nextChangeTime: openTimeStr ? formatTimeString(openTimeStr) : null,
          nextChangeDay: nextDay.day,
          nextChangeIsOpening: true,
        };
      }
    }
    return { isOpen: false, nextChangeTime: null, nextChangeDay: null, nextChangeIsOpening: true };
  }

  // Check if currently open
  let isOpen = false;
  let nextChangeTime: string | null = null;
  let nextChangeDay: string | null = null;
  let nextChangeIsOpening = false;

  if (currentMinutes < todaySchedule.openTime) {
    // Before opening today
    isOpen = false;
    nextChangeIsOpening = true;
    const openTimeStr = todaySchedule.hours.match(/(\d{1,2}:\d{2}\s*[AP]M)/i)?.[1];
    nextChangeTime = openTimeStr ? formatTimeString(openTimeStr) : null;
    nextChangeDay = currentDay; // Opens later today
  } else if (currentMinutes >= todaySchedule.openTime && currentMinutes < todaySchedule.closeTime) {
    // Currently open
    isOpen = true;
    nextChangeIsOpening = false;
    const closeTimeStr = todaySchedule.hours.match(/[-–]\s*(\d{1,2}:\d{2}\s*[AP]M)/i)?.[1];
    nextChangeTime = closeTimeStr ? formatTimeString(closeTimeStr) : null;
    nextChangeDay = currentDay; // Closes later today
  } else {
    // After closing today - find next opening
    isOpen = false;
    nextChangeIsOpening = true;

    for (let i = 1; i <= 7; i++) {
      const nextDayIndex = (currentDayIndex + i) % 7;
      const nextDay = schedule.find(s => s.dayIndex === nextDayIndex);
      if (nextDay && nextDay.openTime !== null) {
        const openTimeStr = nextDay.hours.match(/(\d{1,2}:\d{2}\s*[AP]M)/i)?.[1];
        nextChangeTime = openTimeStr ? formatTimeString(openTimeStr) : null;
        nextChangeDay = nextDay.day;
        break;
      }
    }
  }

  return { isOpen, nextChangeTime, nextChangeDay, nextChangeIsOpening };
};

// Format the status text for display
export const formatOpenStatusText = (status: OpenStatus, timezone?: string): string => {
  if (status.isOpen) {
    if (!status.nextChangeTime) {
      return "Open 24 hours";
    }
    return `Open until ${status.nextChangeTime}`;
  } else {
    if (!status.nextChangeTime) {
      return "Closed";
    }
    const dayPrefix =
      status.nextChangeDay && status.nextChangeDay !== getCurrentDay(timezone)
        ? `, ${getAbbreviatedDay(status.nextChangeDay)}`
        : "";
    return `Closed until ${status.nextChangeTime}${dayPrefix}`;
  }
};
